import './PublicOrderPage.scss';

import { ordersClient } from 'api';
import { MAIN_IMAGE_ROOT } from 'app_config';
import {
  ErrorAlert,
  Helmet,
  ImageComponent,
  LabeledText,
  LoadingIndicator,
  QueryErrorAlert
} from 'components';
import { PublicPage } from 'components/Layout';
import { getFullName } from 'helpers/contactPersonHelpers';
import { EXPIRED_LINK } from 'helpers/errorHandling';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { PublicOrderFormValues } from './components/PublicOrderForm';
import PublicOrderForm from './components/PublicOrderForm';
import { useGetPublicOrderQuery } from './queries';

type RouteParams = 'tenantCode' | 'orderId';

const PublicOrderPage: React.FC = (): React.ReactElement => {
  const { t } = useCustomTranslation();
  const params = useParams<RouteParams>();

  const publicOrderDataQuery = useGetPublicOrderQuery(
    params.tenantCode || '',
    params.orderId || ''
  );

  const [showConfirm, setShowConfirm] = useState(false);

  const publicOrderData = publicOrderDataQuery.data;

  const submitPublicOrder = useCallback(
    async (values: PublicOrderFormValues) => {
      await ordersClient.editPublicOrder(params.tenantCode || '', params.orderId || '', values);
      setShowConfirm(true);
    },
    [params]
  );

  const renderContent = () => {
    return !showConfirm ? (
      <>
        <Helmet title={t('publicOrder.title')} />
        <LabeledText
          label={t('publicOrder.orderCodeLabel')}
          value={publicOrderData ? publicOrderData.order.code : ''}
        />
        <PublicOrderForm
          contactPeople={
            publicOrderData?.customer.contactPeople.map((contactPerson) => ({
              id: contactPerson.id,
              name: getFullName(contactPerson)
            })) || []
          }
          order={publicOrderData?.order}
          onSubmit={submitPublicOrder}
        />
      </>
    ) : (
      <div className="PublicOrderPage_info">
        <p>{t('publicOrder.submitConfirmationText')}</p>
      </div>
    );
  };

  return (
    <PublicPage
      className="PublicOrderPage"
      title={`${t('publicOrder.heading')} ${publicOrderData?.company.name || ''}`}
    >
      <div className="PublicOrderPage_content white-box">
        {publicOrderDataQuery.error && publicOrderDataQuery.error?.response?.status === 404 ? (
          <ErrorAlert errorCode={EXPIRED_LINK} />
        ) : (
          <>
            <div className="PublicOrderPage_content-heading heading">
              <div className="logo">
                <ImageComponent
                  url={
                    publicOrderData?.company.logoUrl
                      ? `${MAIN_IMAGE_ROOT}${publicOrderData.company.logoUrl}`
                      : ''
                  }
                  className="img-fluid"
                />
              </div>
              <span>{`${t('publicOrder.heading')} ${publicOrderData?.company.name || ''}`}</span>
            </div>
            {publicOrderDataQuery.error && (
              <QueryErrorAlert
                error={publicOrderDataQuery.error}
                onRetry={publicOrderDataQuery.refetch}
              />
            )}
            {publicOrderDataQuery.isLoading ? <LoadingIndicator /> : renderContent()}
          </>
        )}
      </div>
    </PublicPage>
  );
};

export default PublicOrderPage;
